import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useAuth } from '@/hooks/useAuth';
import { Repeat, TrendingUp, Users, ArrowRight, Sparkles } from 'lucide-react';

// Public browsable directory of shared DCA templates. Anyone can preview;
// adopting a template requires an authenticated account.
export default function TemplateMarketplace() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('adopts'); // 'adopts' | 'recent'
  const [adopting, setAdopting] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/marketplace/templates?limit=50&sort=${sort}`);
      setTemplates(r.data.templates || []);
    } catch (_e) {
      setTemplates([]);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [sort]);

  const adopt = async (share_id) => {
    if (!user) {
      toast.message('Log in to adopt a template');
      nav('/login');
      return;
    }
    setAdopting(share_id);
    try {
      await api.post(`/wallet/recurring_conversions/adopt/${share_id}`);
      toast.success('Template adopted — see it in your Convert page');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to adopt');
    } finally { setAdopting(null); }
  };

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-10" data-testid="marketplace-page">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <h1 className="font-display text-white text-4xl font-bold tracking-tighter flex items-center gap-3">
            <Sparkles className="w-8 h-8 text-[#FCD535]" /> Template Marketplace
          </h1>
          <p className="text-[#848E9C] text-sm mt-1">
            Discover the most-adopted DCA strategies shared by the community — one click to copy into your account.
          </p>
        </div>
        <div className="inline-flex bg-[#181A20] border border-[#2B3139] rounded-sm" data-testid="marketplace-sort">
          {[
            { k: 'adopts', label: 'Most Adopted' },
            { k: 'recent', label: 'Newest' },
          ].map(s => (
            <button
              key={s.k}
              onClick={() => setSort(s.k)}
              className={`px-3 py-1.5 text-xs uppercase font-mono ${sort === s.k ? 'bg-[#FCD535] text-[#0B0E11]' : 'text-[#848E9C] hover:text-white'}`}
              data-testid={`marketplace-sort-${s.k}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="border border-[#2B3139] bg-[#181A20] p-10 text-center text-[#848E9C]" data-testid="marketplace-loading">
          Loading templates…
        </div>
      ) : templates.length === 0 ? (
        <div className="border border-[#2B3139] bg-[#181A20] p-10 text-center" data-testid="marketplace-empty">
          <div className="text-white font-semibold mb-1">No shared templates yet</div>
          <div className="text-[#848E9C] text-sm mb-4">Be the first — build one in Convert and hit Share to publish it here.</div>
          <Link
            to="/convert"
            className="inline-flex items-center gap-1.5 bg-[#FCD535] hover:bg-[#F0B90B] text-[#0B0E11] rounded-sm px-4 py-2 text-sm font-semibold"
            data-testid="marketplace-goto-convert"
          >
            <Repeat className="w-4 h-4" /> Open Convert
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="marketplace-grid">
          {templates.map(t => (
            <div key={t.share_id} className="border border-[#2B3139] bg-[#181A20] p-4 flex flex-col gap-3 hover:border-[#FCD535]/60 transition-colors"
                 data-testid={`marketplace-card-${t.share_id}`}>
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-white font-semibold text-base leading-tight">{t.label}</div>
                  <div className="text-[10px] font-mono text-[#848E9C] mt-1 flex items-center gap-1">
                    <Users className="w-3 h-3" /> by @{t.owner_name}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase font-mono text-[#5E6673]">Adopts</div>
                  <div className="text-[#FCD535] font-mono text-lg" data-testid={`marketplace-adopts-${t.share_id}`}>
                    {t.adopts}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div className="border border-[#2B3139] bg-[#0B0E11] p-2">
                  <div className="text-[9px] uppercase font-mono text-[#848E9C]">From</div>
                  <div className="text-white font-mono">{t.from_symbol}</div>
                </div>
                <div className="border border-[#2B3139] bg-[#0B0E11] p-2">
                  <div className="text-[9px] uppercase font-mono text-[#848E9C]">Interval</div>
                  <div className="text-white font-mono capitalize">{t.interval}</div>
                </div>
                <div className="border border-[#2B3139] bg-[#0B0E11] p-2">
                  <div className="text-[9px] uppercase font-mono text-[#848E9C]">Amount</div>
                  <div className="text-white font-mono">${t.default_amount.toFixed(0)}</div>
                </div>
                <div className="border border-[#2B3139] bg-[#0B0E11] p-2">
                  <div className="text-[9px] uppercase font-mono text-[#848E9C]">Splits</div>
                  <div className="text-white font-mono">{t.splits_count} coins</div>
                </div>
              </div>
              <div className="flex flex-wrap gap-1">
                {(t.splits || []).slice(0, 6).map((s, i) => (
                  <span key={i} className="text-[10px] font-mono px-1.5 py-0.5 border border-[#2B3139] bg-[#0B0E11] text-[#B7BDC6]" data-testid={`marketplace-split-${t.share_id}-${i}`}>
                    {s.symbol} <span className="text-[#848E9C]">{Math.round((s.weight || 0) * 100)}%</span>
                  </span>
                ))}
              </div>
              <div className="mt-auto flex items-center justify-between gap-2 pt-2 border-t border-[#2B3139]/60">
                <Link
                  to={`/convert/shared/${t.share_id}`}
                  className="text-xs uppercase font-mono text-[#848E9C] hover:text-[#FCD535] flex items-center gap-1"
                  data-testid={`marketplace-preview-${t.share_id}`}
                >
                  Preview <ArrowRight className="w-3 h-3" />
                </Link>
                <button
                  onClick={() => adopt(t.share_id)}
                  disabled={adopting === t.share_id}
                  className="bg-[#FCD535] hover:bg-[#F0B90B] disabled:opacity-50 text-[#0B0E11] rounded-sm font-semibold px-3 py-1.5 text-xs uppercase flex items-center gap-1"
                  data-testid={`marketplace-adopt-${t.share_id}`}
                >
                  <TrendingUp className="w-3 h-3" /> {adopting === t.share_id ? 'Adopting…' : 'Adopt'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
